import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './Button';
import { Link, navigate } from './Router';
import { Logo } from './Logo';

interface NavbarProps {
  currentPath?: string;
}

export const Navbar: React.FC<NavbarProps> = ({ currentPath = '/' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Leistungen', href: '/leistungen' },
    { name: 'Warum wir', href: '/warum-wir' },
    { name: 'Cases', href: '/cases' },
    { name: 'Prozess', href: '/prozess' },
    { name: 'Team', href: '/team' },
  ];

  const handleNavClick = (href: string) => {
    setIsOpen(false);
    navigate(href);
  };

  const isActive = (path: string) => {
    return currentPath === path;
  };

  return (
    <nav className={`sticky top-4 z-50 transition-all duration-300 mx-4 md:mx-8 rounded-2xl ${isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-lg py-3' : 'bg-transparent py-6'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center">
          {/* Logo - Uses height sizing */}
          <Link to="/" className="flex-shrink-0 flex items-center cursor-pointer group">
            <Logo variant="dark" className="h-8 md:h-12" />
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`transition font-bold text-sm uppercase tracking-wide relative group ${
                  isActive(link.href) ? 'text-brand-orange' : 'text-brand-dark hover:text-brand-orange'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-brand-orange transform origin-left transition-transform duration-300 ${
                  isActive(link.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
            ))}
            <Link to="/kontakt">
              <Button variant="primary" className="!py-3 !px-6 text-sm !rounded-xl shadow-orange-500/20">
                Angebot anfordern
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-brand-dark hover:text-brand-orange focus:outline-none transition"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl p-4 mx-0 overflow-hidden border border-gray-100 animate-fade-in-up">
          <div className="space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                className={`block w-full text-left px-4 py-3 rounded-xl text-lg font-bold uppercase transition-colors ${
                  isActive(link.href) ? 'bg-brand-orange text-white shadow-lg' : 'text-brand-dark hover:bg-gray-50 hover:text-brand-orange'
                }`}
              >
                {link.name}
              </button>
            ))}
            <div className="pt-4">
              <Button 
                onClick={() => handleNavClick('/kontakt')}
                fullWidth
                variant="primary"
              >
                Angebot anfordern
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};